import React, { useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { makeStyles, useTheme } from '@material-ui/core'
import customTheme from '../../../styles/mui_theme'

import { v4 as uuidv4 } from 'uuid'
import SingleMaterialPart from './SingleMaterialPart'
import { useTranslation } from 'react-i18next'
import {
  inputTypes,
  itemPriceOptionsObj,
  materialFields
} from '../../../lib/Constants'
import { inputTypeValueTransformer } from '../../../lib/Global'

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    marginLeft: '5px'
  },
  divider: {
    height: '10px'
  }
}))

const emptyItem = {
  name: '',
  description: '',
  quantity: '',
  number: '',
  price: '',
  tax: '',
  total_price: ''
}

const MaterialsParts = ({
  lineItemsList,
  onUpdate,
  section,
  questionId,
  disabled,
  question
}) => {
  const theme = useTheme()
  const enableAmounts =
    question?.item_price ===
    itemPriceOptionsObj.ENABLE_ITEM_PRICE_TAX_TOTAL_PRICE
  const { t } = useTranslation()
  const classes = useStyles()
  const itemsLimit = question.items_limit ?? 1
  const [lineItems, setLineItems] = useState(lineItemsList)
  const [isAddButtonActive, setIsAddButtonActive] = useState()

  useEffect(() => {
    if (!lineItems || lineItems.length === 0) {
      addLineItemHandler()
    }
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkMandatory(lineItems)
      onUpdate(section, questionId, [...lineItems])
    }, 500)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [lineItems])

  const checkMandatory = answer => {
    let fullfilledItems = true
    if (answer?.length === 0) {
      fullfilledItems = false
    }
    if (answer?.length > 0) {
      for (const item of answer) {
        if (!item.quantity || !item.name) {
          fullfilledItems = false
          break
        }
      }
    }
    setIsAddButtonActive(fullfilledItems)
  }

  const addLineItemHandler = () => {
    setLineItems(oldLineItems => {
      const id = uuidv4()
      const newLineItem = {
        ...emptyItem,
        id: id
      }
      if (oldLineItems !== null) {
        return [...oldLineItems, newLineItem]
      } else {
        return [{ ...newLineItem }]
      }
    })
  }

  const removeLineItemHandler = index => {
    setLineItems(prevItems => {
      const newItems = [...prevItems]
      newItems.splice(index, 1)
      return newItems
    })
  }

  function calculateMaterialTotal(materialItem) {
    const amount = +materialItem?.price ?? 0
    const quantity = +materialItem?.quantity ?? 0
    const tax = +materialItem?.tax ?? 0

    const total = amount * quantity
    const totalWithTax = total + (total * tax) / 100

    return totalWithTax
  }

  const updateFieldsHandler = (index, field, value) => {
    setLineItems(prevItems => {
      let modValue = value
      const calculationCondition =
        field === materialFields.QUANTITY ||
        field === materialFields.PRICE ||
        field === materialFields.TAX
      if (field === materialFields.TAX && !!modValue && +modValue > 100) {
        modValue = '100'
      }
      if (calculationCondition) {
        modValue = inputTypeValueTransformer(inputTypes.NUMERIC_ONLY, modValue)
      }
      const newItems = [...prevItems]
      newItems[index][field] = modValue
      if (calculationCondition) {
        const calculatedTotal = calculateMaterialTotal(
          newItems[index]
        ).toString()
        newItems[index][materialFields.TOTAL_PRICE] = calculatedTotal
      }
      return newItems
    })
  }

  const AddButton = () => {
    return (
      <Button
        // this does not work as a class
        sx={{
          textTransform: 'none',
          color: customTheme.colors.blue
        }}
        size="small"
        startIcon={<FontAwesomeIcon icon="fa-solid fa-circle-plus" />}
        onClick={addLineItemHandler}
        disabled={disabled || !isAddButtonActive}
      >
        {t('work_orders.line_item.material')}
      </Button>
    )
  }

  function getSubtotal() {
    let subTotal = 0
    if (!lineItems) return (0).toFixed(2)
    for (const item of lineItems) {
      if (item.total_price) {
        subTotal += +item.total_price
      }
    }
    return subTotal?.toFixed(2)
  }

  function SubTotalComponent() {
    const str = '-'
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontSize: '18px' }}>
          {str.repeat(7)} {getSubtotal()} {str.repeat(7)}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            color: theme.colors.grayAlt
          }}
        >
          {t('subtotal')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={classes.container}>
      {lineItems &&
        lineItems?.map((item, index) => {
          return (
            <SingleMaterialPart
              question={question}
              hideRightAction={lineItems?.length === 1}
              key={index}
              index={index}
              item={item}
              onChange={updateFieldsHandler}
              onRemove={removeLineItemHandler}
              disabled={disabled}
            />
          )
        })}
      {enableAmounts && <SubTotalComponent />}
      {lineItems && lineItems.length < itemsLimit && !disabled && <AddButton />}
      {disabled && <div className={classes.divider} />}
    </Box>
  )
}

export default MaterialsParts
